import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faCheck, faClonePlus, faExternalLinkAlt, faEyeSlash, faMinus, faTimes, faXmark } from '@/fontawesome.ts';
import TradeTypeBadge from '@/components/daytrading/components/TradeTypeBadge.tsx';
import TradeStatusBadge from '@/components/daytrading/components/TradeStatusBadge.tsx';
import MigratedTradeIcon from '@/components/daytrading/components/MigratedTradeIcon.tsx';
import {formatUSDCentValue, toUSDCentValue} from '@/utils/helpers.ts';
import { calculateDayTrade } from '@/utils/daytrade-calculations.ts';
import { getProfitColorClass } from '@/utils/daytrade-session-profit.ts';
import type { DayTrade } from '@/models/day-trading-session.ts';
import type { DayTradeAdjustementID } from '@/models/ids.ts';
import type { DayTradeState, DayTradeStockCountMode } from '@/models/enums.ts';
import type { USDCent } from '@/models/base-types.ts';
import Tooltip from "@/components/ui/Tooltip.tsx";

interface ParticipantTradeCardProps {
  trade: DayTrade;
  budget: USDCent;
  processedAdjustements: DayTradeAdjustementID[];
  skippedAdjustements: DayTradeAdjustementID[];
  overrideAdjustementPrices: { [key: string]: USDCent };
  overrideAdjustmentStockCount: { [key: string]: number };
  stockCountMode: DayTradeStockCountMode;
  isHiddenTrade: boolean;
  sessionClosed: boolean;
  onToggleHidden: () => void;
  onToggleAdjustement: (adjustmentId: DayTradeAdjustementID) => void;
  onOverrideAdjustement: (adjId: DayTradeAdjustementID, priceCents: USDCent | null) => void;
  onOverrideAdjustmentStockCount: (adjId: DayTradeAdjustementID, count: number | null) => void;
  onToggleStockCountMode: () => void;
}

function fmt(n: number, d = 2): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtShares(n: number): string {
  return parseFloat(n.toFixed(4)).toLocaleString('de-DE', { maximumFractionDigits: 4 });
}

function getCardBorderClass(state: DayTradeState): string {
  switch (state) {
    case 'GOAL_REACHED': return 'border-green-600';
    case 'STOPPED':      return 'border-gray-300 dark:border-gray-700';
    case 'STOPLOSS':     return 'border-red-800';
    default: return 'border-gray-200 dark:border-gray-700';
  }
}

function getContentStateClass(state: DayTradeState): string {
  switch (state) {
    case 'GOAL_REACHED': return 'opacity-75 bg-green-50  dark:bg-green-950/30';
    case 'STOPPED':      return 'opacity-45 bg-gray-50   dark:bg-gray-950/30';
    case 'STOPLOSS':     return 'opacity-75 bg-red-50    dark:bg-red-950/30';
    default:             return '           bg-white     dark:bg-gray-800';
  }
}

export default function ParticipantTradeCard({
  trade,
  budget,
  processedAdjustements,
  skippedAdjustements,
  overrideAdjustementPrices,
  overrideAdjustmentStockCount,
  stockCountMode,
  isHiddenTrade,
  sessionClosed,
  onToggleHidden,
  onToggleAdjustement,
  onOverrideAdjustement,
  onOverrideAdjustmentStockCount,
  onToggleStockCountMode,
}: ParticipantTradeCardProps) {
  const [editingAdjId, setEditingAdjId] = useState<DayTradeAdjustementID | null>(null);
  const [adjPriceInput, setAdjPriceInput] = useState('');
  const [editingStockCountAdjId, setEditingStockCountAdjId] = useState<DayTradeAdjustementID | null>(null);
  const [stockCountInput, setStockCountInput] = useState('');

  const calculations = useMemo(() => {
    return calculateDayTrade({
      trade: trade,
      budget: budget,
      mode: 'participant',
      processedAdjustements: processedAdjustements,
      skippedAdjustements: skippedAdjustements,
      overrideAdjustementPrices: overrideAdjustementPrices,
      overrideAdjustmentStockCount: overrideAdjustmentStockCount,
      stockCountMode: stockCountMode,
    });
  }, [trade, budget, processedAdjustements, skippedAdjustements, overrideAdjustementPrices, overrideAdjustmentStockCount, stockCountMode]);

  const shareLabel = useMemo(() => {
    const inv = 1 / trade.share;
    const rounded = Math.round(inv);
    return `1/${rounded}`;
  }, [trade.share]);

  // Calculate "natural" shares (without override) for comparison
  const getNaturalShares = (adjIndex: number): number => {
    const adj = trade.adjustments[adjIndex];
    const effectivePrice = overrideAdjustementPrices[adj.id] ?? adj.entryPrice;
    const entryPriceDollars = effectivePrice / 100;
    let shares = entryPriceDollars > 0 ? Math.floor(calculations.perEntry / entryPriceDollars) : 0;
    if (adjIndex > 0 && stockCountMode === 'FIRST') {
      const firstAdj = trade.adjustments[0];
      const firstPrice = overrideAdjustementPrices[firstAdj.id] ?? firstAdj.entryPrice;
      const firstPriceDollars = firstPrice / 100;
      shares = firstPriceDollars > 0 ? Math.floor(calculations.perEntry / firstPriceDollars) : 0;
    }
    return shares;
  };

  return (
    <div className={`relative rounded-2xl border shadow-sm min-w-90 ${getCardBorderClass(trade.state)} ${isHiddenTrade ? 'grayscale cursor-pointer' : ''}`} onClick={isHiddenTrade ? onToggleHidden : undefined}>

      <div className={`rounded-2xl p-5 transition-opacity h-full flex flex-col ${getContentStateClass(trade.state)} ${trade.migratedFrom !== null ? 'bg-migrated-stripes' : ''} ${isHiddenTrade ? 'opacity-15 pointer-events-none' : ''}`}>

        {/* Header: Symbol + Badge + Mode Toggle + Hide */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <a
              href={`https://www.tradingview.com/symbols/${trade.symbol}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors mr-2"
              title="Auf TradingView öffnen"
              onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
            </a>
            <span className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">{trade.symbol}</span>
            <TradeTypeBadge type={trade.type} />
            {trade.migratedFrom !== null && <MigratedTradeIcon created={trade.created} />}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleStockCountMode}
              className={`text-sm leading-none px-1 cursor-pointer transition-colors ${
                stockCountMode === 'FIRST'
                  ? 'text-blue-500 dark:text-blue-400'
                  : 'text-gray-500 dark:text-gray-500 hover:text-gray-300'
              }`}
              title={stockCountMode === 'NORMAL' ? 'Aktienberechnung: Normal' : 'Aktienberechnung: Erster Einstieg'}
            >
              <FontAwesomeIcon icon={stockCountMode === 'NORMAL' ? faCalculator : faClonePlus} className="w-4 h-4" />
            </button>
            <button
              onClick={onToggleHidden}
              className="text-gray-500 dark:text-gray-500 hover:text-gray-300 cursor-pointer text-lg leading-none px-1"
              title="Ausblenden">
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Investment info line */}
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          {shareLabel} &rarr; <strong className="text-gray-800 dark:text-gray-200">${fmt(calculations.perEntry)}</strong> / Einstieg
          {calculations.adjustments.length > 0 && ( <span className="text-gray-500 dark:text-gray-500"> = ${fmt(calculations.totalInvested)} gesamt</span> )}
        </div>

        {/* Einstiege label */}
        <div className="text-[0.7rem] text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-2 font-semibold">
          Einstiege
        </div>

        {/* Adjustment rows */}
        <div className="space-y-1.5 mb-3">
          {trade.adjustments.map((adj, index) => {
            const calc = calculations.adjustments[index];
            const hasStockCountOverride = overrideAdjustmentStockCount[adj.id] !== undefined;
            const naturalShares = getNaturalShares(index);

            let shouldAnimate = calc.isNew;
            if (isHiddenTrade) shouldAnimate = false;
            if (trade.state === 'GOAL_REACHED') shouldAnimate = false;
            if (trade.state === 'STOPPED') shouldAnimate = false;
            if (trade.state === 'STOPLOSS') shouldAnimate = false;
            if (sessionClosed) shouldAnimate = false;

            return (
              <div
                key={adj.id}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 transition-opacity ${
                  calc.isSkipped ? 'opacity-40' : ''
                } ${shouldAnimate ? 'animate-attention-pulse' : 'bg-gray-100 dark:bg-gray-900/60'}`}
              >
                {/* Toggle button: Neutral → Processed → Skipped → Processed → ... */}
                <button
                  onClick={() => onToggleAdjustement(adj.id)}
                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 cursor-pointer transition-colors ${
                    calc.isProcessed
                      ? 'bg-green-500 text-white'
                      : calc.isSkipped
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-400 hover:bg-green-200 dark:hover:bg-green-900'
                  }`}
                >
                  <FontAwesomeIcon icon={calc.isProcessed ? faCheck : calc.isSkipped ? faTimes : faMinus} style={{ fontSize: '0.6em' }} />
                </button>

                {/* Number */}
                <span className="text-[0.7rem] text-gray-500 dark:text-gray-500 font-semibold w-5 shrink-0">
                  #{index + 1}
                </span>

                {/* Price */}
                {editingAdjId === adj.id ? (
                  <div className="flex items-center gap-1 flex-1">
                    <input
                      type="text"
                      value={adjPriceInput}
                      onChange={(e) => setAdjPriceInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = parseFloat(adjPriceInput.replace(',', '.'));
                          if (!isNaN(val) && val > 0) {
                            const newCents = toUSDCentValue(val);
                            onOverrideAdjustement(adj.id, newCents === adj.entryPrice ? null : newCents);
                          }
                          setEditingAdjId(null);
                        }
                        if (e.key === 'Escape') setEditingAdjId(null);
                      }}
                      className="w-24 px-2 py-0.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      autoFocus
                      step="0.01"
                    />
                    <button
                      onClick={() => {
                        const val = parseFloat(adjPriceInput.replace(',', '.'));
                        if (!isNaN(val) && val > 0) {
                          const newCents = toUSDCentValue(val);
                          onOverrideAdjustement(adj.id, newCents === adj.entryPrice ? null : newCents);
                        }
                        setEditingAdjId(null);
                      }}
                      className="text-blue-500 hover:text-blue-400 text-xs font-bold cursor-pointer"
                    >
                      OK
                    </button>
                    <button onClick={() => setEditingAdjId(null)} className="text-gray-400 hover:text-gray-300 text-xs cursor-pointer">✕</button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      const effectivePrice = overrideAdjustementPrices[adj.id] ?? adj.entryPrice;
                      setAdjPriceInput((effectivePrice / 100).toString());
                      setEditingAdjId(adj.id);
                    }}
                    className="text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-400 dark:hover:text-blue-400 cursor-pointer transition-colors flex-1 text-left"
                  >
                    {overrideAdjustementPrices[adj.id] !== undefined ? (
                      <>
                        <span className="line-through text-gray-400">{formatUSDCentValue(adj.entryPrice)}</span>
                        {' '}
                        <span>{formatUSDCentValue(overrideAdjustementPrices[adj.id])}</span>
                      </>
                    ) : (
                      formatUSDCentValue(adj.entryPrice)
                    )}
                  </button>
                )}

                {/* Allocation */}
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  ${fmt(calc.allocated)}
                </span>

                {/* Shares */}
                {editingStockCountAdjId === adj.id ? (
                  <div className="flex items-center gap-1 min-w-[68px]">
                    <input
                      type="text"
                      value={stockCountInput}
                      onChange={(e) => setStockCountInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = parseInt(stockCountInput);
                          if (!isNaN(val) && val >= 0) {
                            onOverrideAdjustmentStockCount(adj.id, val === naturalShares ? null : val);
                          }
                          setEditingStockCountAdjId(null);
                        }
                        if (e.key === 'Escape') setEditingStockCountAdjId(null);
                      }}
                      className="w-16 px-1 py-0.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      autoFocus
                      step="1"
                      min="0"
                    />
                    <button
                      onClick={() => {
                        const val = parseInt(stockCountInput);
                        if (!isNaN(val) && val >= 0) {
                          onOverrideAdjustmentStockCount(adj.id, val === naturalShares ? null : val);
                        }
                        setEditingStockCountAdjId(null);
                      }}
                      className="text-blue-500 hover:text-blue-400 text-xs font-bold cursor-pointer"
                    >
                      OK
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setStockCountInput(calc.shares.toString());
                      setEditingStockCountAdjId(adj.id);
                    }}
                    className="text-sm font-bold text-purple-400 text-right min-w-[68px] hover:text-blue-400 cursor-pointer transition-colors"
                  >
                    {hasStockCountOverride ? (
                      <>
                        <span className="line-through text-gray-400">{fmtShares(naturalShares)}</span>
                        {' '}
                        <span>{fmtShares(calc.shares)}</span>
                      </>
                    ) : (
                      fmtShares(calc.shares)
                    )}{' '}Ak.
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* spacer */}
        <div className="grow"></div>

        {/* Summary */}
        {trade.adjustments.length > 0 && (
          <div className="bg-gray-100 dark:bg-gray-900/60 rounded-xl p-3 mb-3 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Aktien gesamt</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200">{fmtShares(calculations.totalShares)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Ø Einstieg</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200">${fmt(calculations.avgPrice)}</span>
            </div>
            {trade.state !== 'OPEN' && trade.state !== 'STOPPED' && trade.exitPrice !== null && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Ausstieg (Moderator)</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">${formatUSDCentValue(trade.exitPrice)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Kurs &rarr; Ziel</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {`Ø $${fmt(calculations.avgPrice)}`}
                <span className="text-gray-500"> &rarr; </span>
                <span className="text-purple-400">${fmt(calculations.targetPriceDelta)}</span>
                <span className="text-gray-500"> = </span>
                <span className="text-green-500 font-bold">${fmt(calculations.targetPriceAbsolute)}</span>
              </span>
            </div>
          </div>
        )}

        {/* Profit + State Badge */}
        {trade.adjustments.length > 0 && (
          <div className="flex items-end justify-between">
            <Tooltip text={calculations.isGoalReachedOrStopLoss ? 'Effektiver Gewinn anhand des gesetzten Exit-Preises' : 'Theoretischer Gewinn auf Bases des Ziel-Preises'}>
              <div className={`text-xl font-extrabold ${getProfitColorClass(calculations.profit)}`}>
                ${fmt(calculations.profit)}
                <span className="text-sm font-medium opacity-85 ml-1">
                  ({fmt(calculations.profitPct)}%)
                </span>
              </div>
            </Tooltip>
            <TradeStatusBadge state={trade.state} />
          </div>
        )}
      </div>
      {isHiddenTrade && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <FontAwesomeIcon icon={faEyeSlash} className="text-[3rem] text-gray-400 dark:text-gray-500 opacity-60" />
        </div>
      )}
    </div>
  );
}
